import { FilePlus, FileText, Loader2, User } from 'lucide-react'
import { DatePicker } from '@/components/ui/DatePicker'
import { formatAmount, formatCurrencyInput, formatPhone } from '@/lib/utils/formatters'
import type { ChargebackInvestigationTabProps } from '../../types/components'
import {
  CARD_BRAND_OPTIONS,
  CARD_MERCHANT_OPTIONS,
  CHARGEBACK_DETAIL_CARD_CLASS,
  CHARGEBACK_DETAIL_INPUT_CLASS,
  CHARGEBACK_DETAIL_LABEL_CLASS,
  CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS,
  CHARGEBACK_DETAIL_SECONDARY_BUTTON_CLASS,
  CHARGEBACK_DETAIL_VALUE_CLASS,
  CHARGEBACK_REASON_OPTIONS,
  CHARGEBACK_STATUS_OPTIONS,
  INVESTIGATION_FILE_TYPES,
  SPOKE_RESIDENT_OPTIONS,
  YES_NO_OPTIONS,
} from '../../constants/chargebackDetailConstants'
import { display, formatDate, fullName, isDecisionAllowed } from '../../utils/chargebackDetailFormatters'
import { ChargebackDetailField } from './ChargebackDetailField'
import { chargebackDetailSelectOptions } from './chargebackDetailSelectOptions'
import { ChargebackFileUploadSection } from './ChargebackFileUploadSection'

export function ChargebackInvestigationTab({ applicantId, page }: ChargebackInvestigationTabProps) {
  const {
    detail,
    applicationInfo,
    applicationData,
    investigation,
    patchInvestigation,
    saving,
    fileBusy,
    noteDraft,
    setNoteDraft,
    saveActiveTab,
    addNote,
    handleUpload,
    handleDownloadPacket,
    setPacketConfirmOpen,
    setDeleteFileId,
  } = page

  const address = applicationData?.current_address
  const agent = applicationInfo?.agent?.user_id
    ? applicationInfo.agent
    : applicationInfo?.other_agent ?? applicationInfo?.temp_agent

  const applicantName =
    fullName(
      applicationData?.user_data?.first_name,
      applicationData?.user_data?.last_name,
    ) || '-'

  const formattedAddress = address
    ? `${display(address.house_no)}, ${display(address.street)}${address.apartment ? `, ${address.apartment}` : ''}, ${display(address.city)}, ${display(address.state)}, ${display(address.zip_code)}`
    : '-'

  const leasingAgentName =
    agent?.first_name || agent?.last_name
      ? `${display(agent.first_name)} ${display(agent.last_name)}`
      : agent?.agent_name || '-'

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_24rem] 2xl:grid-cols-[minmax(0,1fr)_26rem]">
      {/* Left Card: Chargeback Details */}
      <section className={`${CHARGEBACK_DETAIL_CARD_CLASS} p-6`}>
        <div className="flex items-center gap-2.5 mb-6">
          <FileText className="h-5 w-5 text-[#1597ff] shrink-0" aria-hidden />
          <h2 className="text-base font-bold text-slate-900">Chargeback Details</h2>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
          <ChargebackDetailField label="Chargeback Status">
            <select
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.chargeback_status}
              onChange={(e) => patchInvestigation({ chargeback_status: e.target.value })}
            >
              {chargebackDetailSelectOptions(CHARGEBACK_STATUS_OPTIONS, 'Select Status')}
            </select>
          </ChargebackDetailField>

          <ChargebackDetailField label="Chargeback Reason">
            <select
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.adjustment_reason}
              onChange={(e) => patchInvestigation({ adjustment_reason: e.target.value })}
            >
              {chargebackDetailSelectOptions(CHARGEBACK_REASON_OPTIONS, 'Select Reason')}
            </select>
          </ChargebackDetailField>

          <ChargebackDetailField label="Case Number">
            <input
              type="text"
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.case_number}
              onChange={(e) => patchInvestigation({ case_number: e.target.value })}
            />
          </ChargebackDetailField>

          <ChargebackDetailField label="Transaction #">
            <input
              type="text"
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.transaction_id}
              onChange={(e) => patchInvestigation({ transaction_id: e.target.value })}
            />
          </ChargebackDetailField>

          <ChargebackDetailField label="Chargeback Amount">
            <input
              type="text"
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              disabled={!isDecisionAllowed(detail)}
              value={investigation.chargeback_amount}
              onChange={(e) =>
                patchInvestigation({ chargeback_amount: formatCurrencyInput(e.target.value) })
              }
              onBlur={() =>
                patchInvestigation({
                  chargeback_amount: formatCurrencyInput(investigation.chargeback_amount, true),
                })
              }
            />
          </ChargebackDetailField>

          <ChargebackDetailField label="Response Deadline">
            <DatePicker
              value={investigation.response_deadline}
              onChange={(value) => patchInvestigation({ response_deadline: value })}
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              showHelperText={false}
              allowFutureDates
            />
          </ChargebackDetailField>

          <ChargebackDetailField label="Original Transaction Date">
            <DatePicker
              value={investigation.transaction_date}
              onChange={(value) => patchInvestigation({ transaction_date: value })}
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              showHelperText={false}
              allowFutureDates
            />
          </ChargebackDetailField>

          <ChargebackDetailField label="Received Date">
            <DatePicker
              value={investigation.chargeback_notification_date}
              onChange={(value) =>
                patchInvestigation({ chargeback_notification_date: value })
              }
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              showHelperText={false}
              allowFutureDates
            />
          </ChargebackDetailField>

          <ChargebackDetailField label="Card Merchant">
            <select
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.card_merchant}
              onChange={(e) =>
                patchInvestigation({
                  card_merchant: e.target.value,
                  ...(e.target.value !== 'CARDCONNECT' ? { card_brand: '', card_no: '' } : {}),
                })
              }
            >
              {chargebackDetailSelectOptions(CARD_MERCHANT_OPTIONS, 'Select Card Merchant')}
            </select>
          </ChargebackDetailField>

          {investigation.card_merchant === 'CARDCONNECT' ? (
            <>
              <ChargebackDetailField label="Card Brand">
                <select
                  className={CHARGEBACK_DETAIL_INPUT_CLASS}
                  value={investigation.card_brand}
                  onChange={(e) => patchInvestigation({ card_brand: e.target.value })}
                >
                  {chargebackDetailSelectOptions(CARD_BRAND_OPTIONS, 'Select Card Brand')}
                </select>
              </ChargebackDetailField>
              <ChargebackDetailField label="Last 4 digits of Card">
                <input
                  type="text"
                  className={CHARGEBACK_DETAIL_INPUT_CLASS}
                  value={investigation.card_no}
                  maxLength={4}
                  onChange={(e) =>
                    patchInvestigation({
                      card_no: e.target.value.replace(/\D/g, '').slice(0, 4),
                    })
                  }
                />
              </ChargebackDetailField>
            </>
          ) : null}

          <ChargebackDetailField label="Transaction IP Address">
            <input
              type="text"
              readOnly
              className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 cursor-default focus:outline-none"
              value={display(detail?.ip_address)}
            />
          </ChargebackDetailField>

          <div className="space-y-2 md:col-span-2">
            <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Spoke with resident</span>
            <div className="flex flex-wrap gap-2.5 sm:gap-3">
              {SPOKE_RESIDENT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="inline-flex cursor-pointer items-center gap-2 select-none rounded-lg border border-slate-200 bg-[#f8fafc] px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                >
                  <input
                    type="checkbox"
                    checked={investigation.spoke_resident.includes(option.value)}
                    onChange={(event) =>
                      patchInvestigation({
                        spoke_resident: event.target.checked
                          ? [...investigation.spoke_resident, option.value]
                          : investigation.spoke_resident.filter((value) => value !== option.value),
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-[#1597ff] focus:ring-[#1597ff]"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          <ChargebackDetailField label="Certificate on File">
            <select
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.certificate_on_file}
              onChange={(e) => patchInvestigation({ certificate_on_file: e.target.value })}
            >
              {chargebackDetailSelectOptions(YES_NO_OPTIONS, 'Select Status')}
            </select>
          </ChargebackDetailField>

          <ChargebackDetailField label="Confirmed Move-In">
            <select
              className={CHARGEBACK_DETAIL_INPUT_CLASS}
              value={investigation.confirmed_move_in}
              onChange={(e) => patchInvestigation({ confirmed_move_in: e.target.value })}
            >
              {chargebackDetailSelectOptions(YES_NO_OPTIONS, 'Select Status')}
            </select>
          </ChargebackDetailField>

          <div className="md:col-span-2">
            <ChargebackFileUploadSection
              title="File upload"
              fieldName="investigation_file"
              fileTypes={INVESTIGATION_FILE_TYPES}
              uploadedType="INVESTIGATION_FILE"
              detail={detail}
              applicantId={applicantId}
              busy={fileBusy}
              onUpload={handleUpload}
              onDelete={setDeleteFileId}
            />
          </div>
        </div>

        <div className="mt-6 pt-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void saveActiveTab()}
            className={CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </button>
        </div>
      </section>

      {/* Right Card: Applicant Information */}
      <aside className={`${CHARGEBACK_DETAIL_CARD_CLASS} p-6 flex flex-col justify-between`}>
        <div className="space-y-5">
          <div className="flex items-center gap-2.5 mb-6">
            <User className="h-5 w-5 text-[#1597ff] shrink-0" aria-hidden />
            <h2 className="text-base font-bold text-slate-900">Applicant Information</h2>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Applicant Name</span>
              <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>{applicantName}</div>
            </div>

            <div className="space-y-1">
              <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Address</span>
              <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>{formattedAddress}</div>
            </div>

            <div className="space-y-1">
              <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Monthly Rent</span>
              <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>
                {formatAmount(applicationInfo?.rent_amount as string | number)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Lease Start</span>
                <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>
                  {formatDate(applicationInfo?.lease_start_date)}
                </div>
              </div>
              <div className="space-y-1">
                <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Actual Move-In</span>
                <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>-</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Property</span>
                <div className={`truncate ${CHARGEBACK_DETAIL_VALUE_CLASS}`}>
                  {display(applicationInfo?.property?.property_name)}
                </div>
              </div>
              <div className="space-y-1">
                <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Management Company</span>
                <div className={`truncate ${CHARGEBACK_DETAIL_VALUE_CLASS}`}>
                  {display(applicationInfo?.property?.company_name)}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Leasing Agent</span>
              <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>{leasingAgentName}</div>
            </div>

            <div className="space-y-1">
              <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Contact Number</span>
              <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>
                {formatPhone(String(agent?.mobile_no ?? agent?.agent_mobile_no ?? ''))}
              </div>
            </div>

            <div className="space-y-1">
              <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Contact Email</span>
              <div className={CHARGEBACK_DETAIL_VALUE_CLASS}>
                {display(agent?.email ?? agent?.agent_email)}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                type="button"
                disabled={saving}
                onClick={() => handleDownloadPacket()}
                className={`${CHARGEBACK_DETAIL_SECONDARY_BUTTON_CLASS} gap-2`}
              >
                <FileText className="h-4 w-4" aria-hidden />
                View CB Packet
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={() => setPacketConfirmOpen(true)}
                className={`${CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS} gap-2`}
              >
                <FilePlus className="h-4 w-4" aria-hidden />
                Gen CB Packet
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-slate-100 pt-6 space-y-3">
          <span className={CHARGEBACK_DETAIL_LABEL_CLASS}>Add Notes</span>
          <textarea
            rows={3}
            placeholder="Type your notes here..."
            className="w-full min-h-[5.5rem] resize-none rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800 placeholder:text-slate-400 transition-colors focus:border-[#1597ff] focus:outline-none focus:ring-1 focus:ring-[#1597ff]"
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
          />
          <button
            type="button"
            disabled={!noteDraft.trim() || saving}
            onClick={() => void addNote()}
            className={CHARGEBACK_DETAIL_PRIMARY_BUTTON_CLASS}
          >
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Add
          </button>
        </div>
      </aside>
    </div>
  )
}
